import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import { useState, useMemo, useCallback } from "react";
import { Box, Button } from "@mui/material";
import { MultiSelectDropdown } from "../multiSelectDropdown";
import { Clear as ClearIcon } from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import {
  Hcp,
  ProgramFiltersWithCountrySpecificFilters,
} from "src/types/programFilters";

type Props = {
  quarters: string[];
  countries: string[];
  hcps: Hcp[];
  programNumbers: string[];
  previousSelectedQuarters: string[];
  previousSelectedCountries: string[];
  previousSelectedHcps: string[];
  previousSelectedProgramNumbers: string[];
  open: boolean;
  onApply: (data: ProgramFiltersWithCountrySpecificFilters) => void;
  onCancel: () => void;
};

export const ProgramFilterDialog = ({
  quarters,
  countries,
  hcps,
  programNumbers,
  previousSelectedQuarters,
  previousSelectedCountries,
  previousSelectedHcps,
  previousSelectedProgramNumbers,
  open,
  onApply,
  onCancel,
}: Props) => {
  // Filter selections
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>(
    previousSelectedQuarters,
  );
  const [selectedHcps, setSelectedHcps] =
    useState<string[]>(previousSelectedHcps);
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    previousSelectedCountries,
  );
  const [selectedProgramNumbers, setSelectedProgramNumbers] = useState<
    string[]
  >(previousSelectedProgramNumbers);

  // Available Hcps for selected country
  const availableHcps = useMemo(() => {
    if (selectedCountries.length === 0) {
      return hcps;
    }
    return hcps.filter((hcp) => selectedCountries.includes(hcp.country));
  }, [selectedCountries]);

  const selectedHcpsPerCountry = useMemo(() => {
    if (selectedCountries.length === 0) {
      return selectedHcps;
    }
    return availableHcps
      .filter((hcp) => selectedHcps.includes(hcp.name))
      .map((hcp) => hcp.name);
  }, [selectedCountries, selectedHcps]);

  // Memo
  // Combine all selected IDs into a single array for rendering chips
  const allSelectedFilters = useMemo(
    () => [
      ...selectedQuarters,
      ...selectedHcpsPerCountry,
      ...selectedCountries,
      ...selectedProgramNumbers,
    ],
    [
      selectedQuarters,
      selectedHcpsPerCountry,
      selectedCountries,
      selectedProgramNumbers,
    ],
  );

  // Unified map of ID to its setter function for dynamic removal
  const setterMap = useMemo(
    () =>
      new Map<string, React.Dispatch<React.SetStateAction<string[]>>>([
        ...selectedQuarters.map((item) => [item, setSelectedQuarters] as const),
        ...selectedHcpsPerCountry.map(
          (item) => [item, setSelectedHcps] as const,
        ),
        ...selectedCountries.map(
          (item) => [item, setSelectedCountries] as const,
        ),
        ...selectedProgramNumbers.map(
          (item) => [item, setSelectedProgramNumbers] as const,
        ),
      ]),
    [
      selectedQuarters,
      selectedHcpsPerCountry,
      selectedCountries,
      selectedProgramNumbers,
    ],
  );

  const handleRemovePill = useCallback(
    (idToRemove: string) => {
      // Find the correct setter function for the item being removed
      const setter = setterMap.get(idToRemove);
      if (setter) {
        setter((prevIds) => prevIds.filter((id) => id !== idToRemove));
      }
    },
    [setterMap],
  );

  // Clear All Filters
  const handleClearAllFilters = useCallback(() => {
    setSelectedQuarters([]);
    setSelectedHcps([]);
    setSelectedCountries([]);
    setSelectedProgramNumbers([]);
  }, []);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Filters</DialogTitle>
      {/* Dropdown and Button container */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: { xs: 2, lg: 0 },
          m: 2,
        }}
      >
        <MultiSelectDropdown
          categoryName="CTV Period"
          items={quarters.map((item) => ({
            id: item,
            label: item,
          }))}
          selectedIds={selectedQuarters}
          onSelectionChange={setSelectedQuarters}
        />
        <MultiSelectDropdown
          categoryName="Program Numbers"
          items={programNumbers.map((item) => ({
            id: item,
            label: item,
          }))}
          selectedIds={selectedProgramNumbers}
          onSelectionChange={setSelectedProgramNumbers}
        />
        <MultiSelectDropdown
          categoryName="Countries"
          items={countries.map((item) => ({
            id: item,
            label: item,
          }))}
          selectedIds={selectedCountries}
          onSelectionChange={setSelectedCountries}
        />
        <MultiSelectDropdown
          categoryName="HCPs"
          items={availableHcps.map((item) => ({
            id: item.name,
            label: item.name,
          }))}
          selectedIds={selectedHcpsPerCountry}
          onSelectionChange={setSelectedHcps}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          m: 2,
        }}
      >
        {allSelectedFilters.map((item) => (
          <Chip
            key={item}
            label={item}
            onDelete={() => handleRemovePill(item)}
            color="primary"
            variant="filled"
            size="medium"
            deleteIcon={<ClearIcon />}
            sx={{
              bgcolor: "secondary.main",
              color: "white",
              "& .MuiChip-deleteIcon": { color: "white" },
            }}
          />
        ))}

        {allSelectedFilters.length > 0 && (
          <Button
            onClick={handleClearAllFilters}
            variant="outlined"
            color="error"
            size="small"
            startIcon={<ClearIcon />}
            sx={{ textTransform: "none", ml: { xs: 0, md: 1 } }}
          >
            Clear All ({allSelectedFilters.length})
          </Button>
        )}
      </Box>
      {(allSelectedFilters.length > 0 ||
        [
          ...previousSelectedCountries,
          ...previousSelectedHcps,
          ...previousSelectedQuarters,
          ...previousSelectedProgramNumbers,
        ].length > 0) && (
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() =>
              onApply({
                countries: selectedCountries,
                hcps: selectedHcpsPerCountry,
                quarters: selectedQuarters,
                hcpsForCountry: availableHcps,
                programNumbers: selectedProgramNumbers,
              })
            }
          >
            Apply
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
